/**
 * Translation Update Handler
 *
 * Listens for 'translations-updated' events and triggers re-renders in Vue components.
 * This enables live updates when translations are loaded asynchronously.
 */

// Reactive state that will be shared across all Vue instances
let translationState = null;
let originalI18n = null;

/**
 * Install the translation updater as a Vue mixin.
 * This wraps the $I18n methods to be reactive, so computed properties
 * that use translations will automatically re-compute when translations update.
 *
 * @param {Vue} Vue - The Vue constructor
 */
export function installTranslationUpdater(Vue) {
	// Create a reactive trigger that changes when translations update
	translationState = Vue.observable({ version: 0 });

	// Listen for translation updates
	window.addEventListener('translations-updated', () => {
		translationState.version++;
	});

	// Store reference to original I18n instance (could be on prototype or window)
	originalI18n = Vue.prototype.$I18n || window.$I18n;

	// Create a reactive wrapper object with getters that trigger dependencies
	// When any getter is accessed, it reads translationState.version which creates
	// a Vue reactive dependency. This works for both this.$I18n and window.$I18n.
	const reactiveI18nWrapper = {
		get trans() {
			const _v = translationState.version; // Create reactive dependency
			return (key, replace = {}) => originalI18n.trans(key, replace);
		},
		get trans_choice() { // eslint-disable-line camelcase
			const _v = translationState.version;
			return (key, count = 1, replace = {}) => originalI18n.trans_choice(key, count, replace);
		},
		get has() {
			const _v = translationState.version;
			return (key) => originalI18n.has(key);
		},
		get isReady() {
			const _v = translationState.version;
			return () => originalI18n.isReady();
		},
		get _original() {
			return originalI18n;
		},
		get _version() {
			return translationState.version;
		},
	};

	// Replace both Vue.prototype.$I18n and window.$I18n with the reactive wrapper
	// This ensures that all access patterns work:
	// - this.$I18n.trans() (Vue component)
	// - $I18n.trans() (global)
	// - window.$I18n.trans() (explicit global)
	Vue.prototype.$I18n = reactiveI18nWrapper;
	window.$I18n = reactiveI18nWrapper;

	// Also provide _translationVersion as a computed property for explicit use
	Vue.mixin({
		computed: {
			_translationVersion() {
				return translationState.version;
			},
		},
	});

	// Expose the state globally for direct access
	window._translationState = translationState;
}

/**
 * Get the current translation version (for use in computed properties)
 * Accessing this in a computed property creates a reactive dependency
 *
 * @returns {number} Current translation version
 */
export function getTranslationVersion() {
	return translationState ? translationState.version : 0;
}

/**
 * Simple listener setup without Vue mixin (for non-Vue contexts)
 * Calls the provided callback when translations are updated.
 *
 * @param {Function} callback - Function to call when translations update
 * @returns {Function} - Cleanup function to remove the listener
 */
export function onTranslationsUpdated(callback) {
	const handler = () => callback();
	window.addEventListener('translations-updated', handler);
	return () => window.removeEventListener('translations-updated', handler);
}

export default { installTranslationUpdater, onTranslationsUpdated, getTranslationVersion };
